import React from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { PlayCircle, Timer, ChevronRight, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import FLAME from "../assets/journey/flame.png";
import { ENDURANCE_EMBLEM } from "../lib/perkAssets";

const GYM_BG =
  "https://images.pexels.com/photos/29392546/pexels-photo-29392546.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

/**
 * Hero streak block — dark gym backdrop fading into cream, a glowing 3D
 * flame with the current streak number, greeting, and the two primary
 * play CTAs. This is the top of the "Fitness Journey" home.
 */
export const HeroStreak = ({ name, streak, hasNext, continueLabel, onContinue, onQuick, endurance, onEndurance }) => {
  const { t } = useTranslation();
  return (
    <motion.div
      data-testid="hero-streak"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-3xl border border-[#E6E2D8]"
    >
      {/* Dark gym backdrop, masked to fade into cream */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${GYM_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          WebkitMaskImage: "linear-gradient(to bottom, black 0%, rgba(0,0,0,0.35) 45%, transparent 78%)",
          maskImage: "linear-gradient(to bottom, black 0%, rgba(0,0,0,0.35) 45%, transparent 78%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ background: "linear-gradient(to bottom, rgba(44,51,42,0.55) 0%, rgba(247,245,240,0.15) 55%, #F7F5F0 100%)" }}
      />

      <div className="relative px-5 pt-5 pb-5 flex flex-col items-center text-center">
        <p
          className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/90 mb-2.5"
          style={{ textShadow: "0 1px 6px rgba(44,51,42,0.6)" }}
        >
          {t("dashboard.hi", { name })}
        </p>

        <div className="relative w-[112px] h-[112px] grid place-items-center">
          <motion.img
            src={FLAME}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_10px_30px_rgba(232,163,101,0.45)]"
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="relative z-10 flex flex-col items-center"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.15, type: "spring", stiffness: 180, damping: 14 }}
          >
            <span
              data-testid="hero-streak-count"
              style={{ fontFamily: "Outfit, sans-serif", textShadow: "0 2px 10px rgba(255,255,255,0.9)" }}
              className="text-4xl font-black leading-none text-[#2C332A] tabular-nums"
            >
              {streak}
            </span>
          </motion.div>
        </div>

        <p
          style={{ fontFamily: "Outfit, sans-serif" }}
          className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-[#B07B3C]"
        >
          {t("dashboard.day_streak_label", "Day Streak")}
        </p>

        <div className="mt-4 flex flex-col items-center gap-2.5 w-full">
          <Button
            data-testid="hero-continue-btn"
            size="lg"
            onClick={onContinue}
            className="rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white h-14 w-full max-w-[420px] text-base font-bold shadow-xl shadow-[#5F7161]/30 active:scale-[0.98] transition-transform"
          >
            <PlayCircle className="mr-2 shrink-0" size={22} strokeWidth={2} />
            <span className="truncate">{hasNext ? continueLabel : t("dashboard.view_journey")}</span>
          </Button>
          <button
            data-testid="hero-quick-btn"
            type="button"
            onClick={onQuick}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#5F7161] hover:text-[#4D5D4F] active:scale-95 transition-transform"
          >
            <Timer className="shrink-0" size={15} strokeWidth={2} />
            <span className="whitespace-nowrap underline underline-offset-4 decoration-[#8AA48F]/50">{t("dashboard.quick_session")}</span>
          </button>
        </div>

        {/* Endurance CTA — always visible so players know there's more to
            play beyond the Journey / daily / weekly. Locked with progress
            until the Journey is finished, then "keep running". */}
        {endurance && !endurance.unlocked && (
          <motion.button
            data-testid="hero-endurance-cta"
            type="button"
            onClick={onEndurance}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mt-3 w-full max-w-[420px] relative overflow-hidden rounded-2xl border border-[#E6E2D8] bg-white active:scale-[0.98] transition-transform"
          >
            <div className="flex items-center gap-3 p-2.5 pr-3 text-left">
              <div className="relative shrink-0 w-14 h-14 rounded-xl bg-[#EFF1EC] grid place-items-center overflow-hidden">
                <img
                  src={ENDURANCE_EMBLEM}
                  alt=""
                  aria-hidden="true"
                  className={`w-12 h-12 object-contain ${endurance.unlocked ? "" : "grayscale opacity-70"}`}
                />
                {!endurance.unlocked && (
                  <div className="absolute inset-0 grid place-items-center bg-black/25">
                    <Lock size={16} strokeWidth={2} className="text-white" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#B07B3C]">
                    {t("endurance.mode", "Endurance Mode")}
                  </p>
                  {endurance.unlocked && (endurance.miles || 0) === 0 && (
                    <span
                      data-testid="endurance-new-badge"
                      className="text-[9px] font-black uppercase tracking-wide bg-[#E8A365] text-white rounded-full px-1.5 py-0.5"
                    >
                      {t("common.new", "NEW")}
                    </span>
                  )}
                </div>
                <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-sm font-semibold text-[#2C332A] leading-tight truncate">
                  {endurance.unlocked
                    ? t("endurance.cta_unlocked", "{{miles}} miles logged — keep running", { miles: endurance.miles || 0 })
                    : t("endurance.cta_locked", "Finish your Journey to unlock")}
                </p>
                {!endurance.unlocked && (
                  <div className="mt-1 flex items-center gap-2">
                    <div className="h-1.5 flex-1 rounded-full bg-[#E6E2D8] overflow-hidden">
                      <div
                        className="h-full bg-[#5F7161]"
                        style={{ width: `${Math.min(100, Math.round(((endurance.journey_completed || 0) / Math.max(1, endurance.journey_total_levels || 1)) * 100))}%` }}
                      />
                    </div>
                    <span className="text-[10px] font-bold text-[#2C332A]/60 tabular-nums shrink-0">
                      {endurance.journey_completed || 0}/{endurance.journey_total_levels || 0}
                    </span>
                  </div>
                )}
              </div>
              <ChevronRight size={18} strokeWidth={2} className="text-[#8AA48F] shrink-0" />
            </div>
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

export default HeroStreak;
